/**
 * Tokens de design da Corretto (identidade de outubro/2026).
 *
 * Fonte unica das cores da plataforma: tema, componentes e o `Colors` legado
 * leem daqui. Hex solto em componente e divida da migracao (ver
 * `scripts/check-brand.mjs`).
 *
 * Regra do verde (decisao D2 do plano CORRETTO): o verde da marca
 * (`verde[500]`) reprova contraste com texto branco (2,7:1). Ele entra como
 * preenchimento com texto azul ou grafite por cima, em icones e em graficos.
 * Texto verde e botao verde com texto branco usam `verdeEscuro` (5,4:1).
 */

export const brand = {
  azul: '#13294B',
  verde: '#1FB46E',
  verdeEscuro: '#127A47',
  grafite: '#2B2F36',
  creme: '#F5F0E8',
  nevoa: '#E3E7EC',
  branco: '#FFFFFF'
} as const

type Scale = Record<
  50 | 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900,
  string
>

// Contraste com branco entre parenteses. Texto normal pede 4,5 ou mais.
export const azul: Scale = {
  50: '#EEF2F8', // 1,1
  100: '#D6DFEC', // 1,3
  200: '#ADBFD9', // 1,9
  300: '#7F98BF', // 2,9
  400: '#5573A3', // 4,8
  500: '#345486', // 7,6
  600: '#23406E', // 10,3
  700: '#1A335C', // 12,6
  800: brand.azul, // 14,5
  900: '#0C1B33' // 17,2
}

export const verde: Scale = {
  50: '#E8F8F0', // 1,1
  100: '#C6EEDB', // 1,3
  200: '#94DFBC', // 1,6
  300: '#5ECD9A', // 2,0
  400: '#34BF80', // 2,4
  500: brand.verde, // 2,7 (so preenchimento)
  600: '#18965B', // 3,8 (texto grande e icone)
  700: brand.verdeEscuro, // 5,4
  800: '#0E5F38', // 7,7
  900: '#0A4428' // 11,2
}

export const neutro: Scale = {
  50: '#F7F8FA', // 1,1
  100: '#EEF1F4', // 1,1
  200: brand.nevoa, // 1,2
  300: '#CDD3DB', // 1,5
  400: '#A7AFBA', // 2,2 (borda, desabilitado)
  500: '#7D8592', // 3,7 (texto grande e icone)
  600: '#5B626E', // 6,2 (texto secundario)
  700: '#434953', // 9,1
  800: brand.grafite, // 13,4
  900: '#1A1D22' // 16,9
}

// Fora da paleta da marca: so para aviso (pendente, vencendo, atencao).
export const ambar: Scale = {
  50: '#FFF6E0', // 1,1
  100: '#FDE9B8', // 1,2
  200: '#F9D27A', // 1,4
  300: '#F2B21B', // 1,8 (so preenchimento, com texto azul)
  400: '#E0A100', // 2,3 (so preenchimento)
  500: '#C98600', // 3,0 (icone)
  600: '#B26A00', // 4,2 (texto grande e icone)
  700: '#8A5300', // 6,3
  800: '#7A4A00', // 7,4
  900: '#5C3700' // 10,4
}

/** Papeis das cores na interface. */
export const semantic = {
  primary: azul[800],
  primaryHover: azul[900],
  primaryLight: azul[600],
  onPrimary: brand.branco,
  secondary: verde[500],
  secondaryHover: verde[600],
  onSecondary: azul[800],
  success: verde[700],
  successBg: verde[50],
  // Vermelho de erro do MUI. Desde a Corretto ele so significa erro/alerta,
  // nunca marca.
  error: '#D32F2F',
  errorDark: '#970700',
  text: brand.grafite,
  textSecondary: neutro[600],
  textDisabled: neutro[400],
  background: brand.creme,
  surface: brand.branco,
  surfaceMuted: neutro[50],
  border: brand.nevoa,
  borderStrong: neutro[300]
} as const

type StatusTone = {
  /** Fundo do chip, card ou alerta. */
  bg: string
  /** Texto e icone sobre `bg` (AA). */
  fg: string
  border: string
  /** Preenchimento forte (barra, ponto, selo). */
  solid: string
  /** Texto sobre `solid` (AA). */
  onSolid: string
}

/**
 * Tons de status (ativo, pendente, recusado, informativo, inativo). Telas de
 * status usam estes pares em vez de hex: o par bg/fg ja passa AA.
 */
export const status: Record<
  'sucesso' | 'aviso' | 'erro' | 'info' | 'neutro',
  StatusTone
> = {
  sucesso: {
    bg: verde[50],
    fg: verde[800],
    border: verde[200],
    solid: verde[500],
    onSolid: azul[800]
  },
  aviso: {
    bg: ambar[50],
    fg: ambar[700],
    border: ambar[200],
    solid: ambar[300],
    onSolid: azul[800]
  },
  erro: {
    bg: '#FDECEC',
    fg: '#B3261E',
    border: '#F5C2BF',
    solid: semantic.error,
    onSolid: brand.branco
  },
  info: {
    bg: azul[50],
    fg: azul[700],
    border: azul[100],
    solid: azul[500],
    onSolid: brand.branco
  },
  neutro: {
    bg: neutro[100],
    fg: neutro[700],
    border: neutro[300],
    solid: neutro[600],
    onSolid: brand.branco
  }
}

/** Fundos de avatar com iniciais em branco (todos passam AA com branco). */
export const avatarPalette = [
  neutro[700],
  azul[800],
  verde[700],
  ambar[700],
  azul[500],
  verde[800]
] as const

/** Ordem das series em graficos (funil, barras, pizza). */
export const chartPalette = [
  azul[800],
  verde[500],
  azul[400],
  neutro[500],
  verde[300],
  azul[200]
] as const

export const fontFamily = '"Manrope", "Helvetica", "Arial", sans-serif'

export const radius = {
  sm: 6,
  md: 10,
  lg: 16
} as const
